import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import type {
  ActionEntry,
  EntityEntry,
  EntityLoader,
  SearchableEntry,
  FunctionRegistryContextValue,
} from '../types';

const FunctionRegistryContext = createContext<FunctionRegistryContextValue | null>(null);

export interface FunctionRegistryProviderProps {
  children: React.ReactNode;
  actions?: ActionEntry[];
  entityLoaders?: EntityLoader[];
}

export function FunctionRegistryProvider({
  children,
  actions = [],
  entityLoaders = [],
}: FunctionRegistryProviderProps) {
  const [entities, setEntities] = useState<EntityEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const loadedRef = useRef(false);

  const loadEntities = useCallback(async () => {
    if (entityLoaders.length === 0) return;
    
    setIsLoading(true);
    setError(null);

    try {
      const results = await Promise.all(
        entityLoaders.map(async (loader) => {
          try {
            const data = await loader.fetch();
            return data.map(loader.toEntry);
          } catch (err) {
            return [];
          }
        })
      );
      
      setEntities(results.flat());
      loadedRef.current = true;
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load entities'));
    } finally {
      setIsLoading(false);
    }
  }, [entityLoaders]);

  const allEntries = useMemo((): SearchableEntry[] => {
    return [...actions, ...entities];
  }, [actions, entities]);

  const value = useMemo((): FunctionRegistryContextValue => ({
    actions,
    entityLoaders,
    entities,
    isLoading,
    error,
    loadEntities,
    allEntries,
  }), [actions, entityLoaders, entities, isLoading, error, loadEntities, allEntries]);

  return (
    <FunctionRegistryContext.Provider value={value}>
      {children}
    </FunctionRegistryContext.Provider>
  );
}

export function useFunctionRegistry(): FunctionRegistryContextValue {
  const context = useContext(FunctionRegistryContext);
  if (!context) {
    throw new Error('useFunctionRegistry must be used within a FunctionRegistryProvider');
  }
  return context;
}

export function useFunctionRegistryOptional(): FunctionRegistryContextValue | null {
  return useContext(FunctionRegistryContext);
}
